/** @jsx jsx */
import { jsx } from "@emotion/core";
import kebabCase from "lodash.kebabcase";
import React, { useEffect, useRef, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { Trustpilot, TrustpilotTemplates } from "../../Trustpilot";
import { PanelWrapper } from "../../PanelWrapper";

import "./style.scss";

interface ITrustPilotCustomerReviewsObject {
  internal: {
    content?: string; // JSON string
  };
}

export interface ITrustPilotPanelProps {
  header?: string;
  headerAlignment: string;
  noPaddingBottom?: boolean;
  variant?: string;
  customerReviews?: ITrustPilotCustomerReviewsObject;
}

// same as breakpoints defined in trussle-styles
// redefined here so the values can be used programmatically in javascript
enum MediaQueries {
  Small = 480,
  Medium = 640,
  Wide = 960,
  ExtraWide = 1200,
}

const useMeasure = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState({
    height: 0,
    left: 0,
    top: 0,
    width: 0,
  });
  useEffect(() => {
    const ro = new ResizeObserver(([entry]: ResizeObserverEntry[]) => setBounds(entry.contentRect));
    if (ref.current) {
      ro.observe(ref.current);
    }
    return () => ro.disconnect();
  }, [ref.current, setBounds]);
  return [ref, bounds] as [typeof ref, typeof bounds];
};

export const TrustpilotPanel: React.FC<ITrustPilotPanelProps> = ({
  header,
  headerAlignment,
  noPaddingBottom = false,
}) => {
  const [ref, { width }] = useMeasure();

  const textAlign = headerAlignment ? headerAlignment.toLowerCase() : "center";

  const styles = {
    header: {
      fontWeight: "bold" as const,
      fontSize: "2rem",
      margin: "0 auto 50px auto",
      maxWidth: "960px",
      textAlign: textAlign as any,
    },
  };

  return (
    <PanelWrapper className={noPaddingBottom ? "trustpilot-panel-container__no-padding-bottom" : undefined}>
      <div
        role="region"
        aria-label="Trustpilot"
        className="trustpilot-panel-container"
        ref={ref}
      >
        {header && (
          <h2
            {...(header && {
              id: kebabCase(header.toLowerCase()),
            })}
            css={styles.header}
            className="trustpilot-panel-heading"
          >
            {header}
          </h2>
        )}
        <Trustpilot
          template={
            width >= MediaQueries.Medium
              ? TrustpilotTemplates.Carousel
              : TrustpilotTemplates.Slider
          }
        />
      </div>
    </PanelWrapper>
  );
};
